import _ from 'lodash';
import { adminClient as apolloClient } from '@/store/apollo';

const PREVIEW_ENDPOINT = `${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PREVIEW_ROUTE}`;

import BillingPeriodsPaginatedGql from '@/store/graphql/billing/queries/BillingPeriodsPaginated.gql';
import CmsBillingDetailGql from '@/store/graphql/billing/queries/CmsBillingDetail.gql';
import MyBillingDetailGql from '@/store/graphql/billing/queries/MyBillingDetail.gql';
import BillingPeriodHistoryGql from '@/store/graphql/billing/queries/BillingPeriodHistory.gql';
import BillingFeaturesGql from '@/store/graphql/billing/queries/BillingFeatures.gql';
import BillingFeatureMarginsGql from '@/store/graphql/billing/queries/BillingFeatureMargins.gql';
import ProviderCostSummaryGql from '@/store/graphql/billing/queries/ProviderCostSummary.gql';
import ProviderTokenPricesGql from '@/store/graphql/billing/queries/ProviderTokenPrices.gql';
import BillingInvoicesGql from '@/store/graphql/billing/queries/BillingInvoices.gql';

import UpdateCmsFeaturePricingGql from '@/store/graphql/billing/mutations/UpdateCmsFeaturePricing.gql';
import UpdateBillingFeatureCostMarginGql from '@/store/graphql/billing/mutations/UpdateBillingFeatureCostMargin.gql';
import UpdateCmsBillingIntervalGql from '@/store/graphql/billing/mutations/UpdateCmsBillingInterval.gql';
import UpdateCmsBillingSettingsGql from '@/store/graphql/billing/mutations/UpdateCmsBillingSettings.gql';
import CloseBillingPeriodGql from '@/store/graphql/billing/mutations/CloseBillingPeriod.gql';
import CreateBillingPeriodGql from '@/store/graphql/billing/mutations/CreateBillingPeriod.gql';
import CreateBillingLineItemGql from '@/store/graphql/billing/mutations/CreateBillingLineItem.gql';
import ReinitializeRecurringChargesGql from '@/store/graphql/billing/mutations/ReinitializeRecurringCharges.gql';
import MarkPeriodInvoicedGql from '@/store/graphql/billing/mutations/MarkPeriodInvoiced.gql';
import MarkInvoicePaidGql from '@/store/graphql/billing/mutations/MarkInvoicePaid.gql';
import RetryInvoiceChargeGql from '@/store/graphql/billing/mutations/RetryInvoiceCharge.gql';
import BillManualPositionsGql from '@/store/graphql/billing/mutations/BillManualPositions.gql';
import CreateOneOffInvoiceGql from '@/store/graphql/billing/mutations/CreateOneOffInvoice.gql';
import GeneratePeriodInvoiceGql from '@/store/graphql/billing/mutations/GeneratePeriodInvoice.gql';
import UpdateBillingLineItemGql from '@/store/graphql/billing/mutations/UpdateBillingLineItem.gql';
import DeleteBillingLineItemGql from '@/store/graphql/billing/mutations/DeleteBillingLineItem.gql';
import CreateStripeSetupIntentGql from '@/store/graphql/billing/mutations/CreateStripeSetupIntent.gql';
import ConfirmBillingSetupGql from '@/store/graphql/billing/mutations/ConfirmBillingSetup.gql';
import EnableAutoBillingGql from '@/store/graphql/billing/mutations/EnableAutoBilling.gql';

export default {
    fetchDashboard: (data) =>
        new Promise((resolve, reject) => {
            // Convert filters object to array (same pattern as UserApi)
            let filters = [];
            _.each(data.filters, (entry) => filters.push(entry));

            apolloClient
                .query({
                    query: BillingPeriodsPaginatedGql,
                    variables: {
                        currentPage: _.get(data, 'pageInfo.currentPage', 1),
                        perPage: _.get(data, 'pageInfo.perPage', 25),
                        order: _.get(data, 'variables.order', 'name'),
                        direction: _.get(data, 'variables.direction', 'asc'),
                        filters,
                    },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.BillingPeriodsPaginated))
                .catch((err) => reject(err));
        }),

    fetchDetail: (cmsId) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: CmsBillingDetailGql,
                    variables: { cmsId: parseInt(cmsId) },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.CmsBillingDetail))
                .catch((err) => reject(err));
        }),

    fetchMyDetail: (cmsId) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: MyBillingDetailGql,
                    variables: { cmsId: parseInt(cmsId) },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.CmsBillingDetail))
                .catch((err) => reject(err));
        }),

    fetchHistory: (variables) =>
        new Promise((resolve, reject) => {
            if (variables.cmsId) variables.cmsId = parseInt(variables.cmsId);

            apolloClient
                .query({
                    query: BillingPeriodHistoryGql,
                    variables,
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.BillingPeriodHistory))
                .catch((err) => reject(err));
        }),

    fetchFeatures: (type) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: BillingFeaturesGql,
                    variables: { type },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.BillingFeatures))
                .catch((err) => reject(err));
        }),

    // Same feature list, plus the platform margin. Kept separate from
    // fetchFeatures so the markup is only ever requested by super-user screens.
    fetchFeatureMargins: (type) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: BillingFeatureMarginsGql,
                    variables: { type },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.BillingFeatures))
                .catch((err) => reject(err));
        }),

    fetchProviderCostSummary: (variables) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: ProviderCostSummaryGql,
                    variables: {
                        from: _.get(variables, 'from', null),
                        to: _.get(variables, 'to', null),
                    },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.ProviderCostSummary))
                .catch((err) => reject(err));
        }),

    fetchProviderTokenPrices: () =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({ query: ProviderTokenPricesGql, fetchPolicy: 'network-only' })
                .then(({ data }) => resolve(data.ProviderTokenPrices))
                .catch((err) => reject(err));
        }),

    updateFeatureCostMargin: (variables) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: UpdateBillingFeatureCostMarginGql,
                    variables: {
                        billing_feature_cost_id: parseInt(variables.billingFeatureCostId),
                        margin_multiplier: variables.marginMultiplier,
                    },
                })
                .then(({ data }) => resolve(data.UpdateBillingFeatureCostMargin))
                .catch((err) => reject(err));
        }),

    updatePricing: (variables) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: UpdateCmsFeaturePricingGql,
                    variables: {
                        cmsId: parseInt(variables.cmsId),
                        pricing: variables.pricing,
                    },
                })
                .then(({ data }) => resolve(data.UpdateCmsFeaturePricing))
                .catch((err) => reject(err));
        }),

    updateInterval: (variables) =>
        new Promise((resolve, reject) => {
            if (variables.cmsId) variables.cmsId = parseInt(variables.cmsId);

            apolloClient
                .mutate({
                    mutation: UpdateCmsBillingIntervalGql,
                    variables,
                })
                .then(({ data }) => resolve(data.UpdateCmsBillingInterval))
                .catch((err) => reject(err));
        }),

    updateBillingSettings: (variables) =>
        new Promise((resolve, reject) => {
            if (variables.cmsId) variables.cmsId = parseInt(variables.cmsId);

            apolloClient
                .mutate({
                    mutation: UpdateCmsBillingSettingsGql,
                    variables,
                })
                .then(({ data }) => resolve(data.UpdateCmsBillingSettings))
                .catch((err) => reject(err));
        }),

    closePeriod: (variables) =>
        new Promise((resolve, reject) => {
            if (variables.billingPeriodId) variables.billingPeriodId = parseInt(variables.billingPeriodId);
            if (variables.prorateMonths) variables.prorateMonths = parseInt(variables.prorateMonths);

            apolloClient
                .mutate({
                    mutation: CloseBillingPeriodGql,
                    variables,
                })
                .then(({ data }) => resolve(data.CloseBillingPeriod))
                .catch((err) => reject(err));
        }),

    createLineItem: (variables) =>
        new Promise((resolve, reject) => {
            if (variables.billingPeriodId) variables.billingPeriodId = parseInt(variables.billingPeriodId);
            if (variables.billingFeatureId) variables.billingFeatureId = parseInt(variables.billingFeatureId);

            apolloClient
                .mutate({
                    mutation: CreateBillingLineItemGql,
                    variables,
                })
                .then(({ data }) => resolve(data.CreateBillingLineItem))
                .catch((err) => reject(err));
        }),

    reinitializeRecurringCharges: (variables) =>
        new Promise((resolve, reject) => {
            if (variables.billingPeriodId) variables.billingPeriodId = parseInt(variables.billingPeriodId);

            apolloClient
                .mutate({
                    mutation: ReinitializeRecurringChargesGql,
                    variables,
                })
                .then(({ data }) => resolve(data.ReinitializeRecurringCharges))
                .catch((err) => reject(err));
        }),

    createPeriod: (variables) =>
        new Promise((resolve, reject) => {
            if (variables.cmsId) variables.cmsId = parseInt(variables.cmsId);

            apolloClient
                .mutate({
                    mutation: CreateBillingPeriodGql,
                    variables,
                })
                .then(({ data }) => resolve(data.CreateBillingPeriod))
                .catch((err) => reject(err));
        }),

    markPeriodInvoiced: (variables) =>
        new Promise((resolve, reject) => {
            if (variables.billingPeriodId) variables.billingPeriodId = parseInt(variables.billingPeriodId);

            apolloClient
                .mutate({
                    mutation: MarkPeriodInvoicedGql,
                    variables,
                })
                .then(({ data }) => resolve(data.MarkPeriodInvoiced))
                .catch((err) => reject(err));
        }),

    retryInvoiceCharge: ({ invoiceId }) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: RetryInvoiceChargeGql,
                    variables: { invoice_id: parseInt(invoiceId) },
                })
                .then(({ data }) => resolve(data.RetryInvoiceCharge))
                .catch((err) => reject(err));
        }),
    markInvoicePaid: ({ invoiceId }) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: MarkInvoicePaidGql,
                    variables: { invoice_id: parseInt(invoiceId) },
                })
                .then(({ data }) => resolve(data.MarkInvoicePaid))
                .catch((err) => reject(err));
        }),

    billManualPositions: ({ billingPeriodId, positions }) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: BillManualPositionsGql,
                    variables: { billing_period_id: parseInt(billingPeriodId), positions },
                })
                .then(({ data }) => resolve(data.BillManualPositions))
                .catch((err) => reject(err));
        }),

    createOneOffInvoice: ({ cmsId, positions, sendEmail }) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CreateOneOffInvoiceGql,
                    variables: { cms_id: parseInt(cmsId), positions, send_email: sendEmail },
                })
                .then(({ data }) => resolve(data.CreateOneOffInvoice))
                .catch((err) => reject(err));
        }),

    generatePeriodInvoice: (variables) =>
        new Promise((resolve, reject) => {
            if (variables.billingPeriodId) variables.billingPeriodId = parseInt(variables.billingPeriodId);

            apolloClient
                .mutate({
                    mutation: GeneratePeriodInvoiceGql,
                    variables,
                })
                .then(({ data }) => resolve(data.GeneratePeriodInvoice))
                .catch((err) => reject(err));
        }),

    updateLineItem: (variables) =>
        new Promise((resolve, reject) => {
            if (variables.id) variables.id = parseInt(variables.id);

            apolloClient
                .mutate({
                    mutation: UpdateBillingLineItemGql,
                    variables,
                })
                .then(({ data }) => resolve(data.UpdateBillingLineItem))
                .catch((err) => reject(err));
        }),

    deleteLineItem: (variables) =>
        new Promise((resolve, reject) => {
            if (variables.id) variables.id = parseInt(variables.id);

            apolloClient
                .mutate({
                    mutation: DeleteBillingLineItemGql,
                    variables,
                })
                .then(({ data }) => resolve(data.DeleteBillingLineItem))
                .catch((err) => reject(err));
        }),

    createStripeSetupIntent: (cmsId) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CreateStripeSetupIntentGql,
                    variables: { cmsId: parseInt(cmsId) },
                })
                .then(({ data }) => resolve(data.CreateStripeSetupIntent))
                .catch((err) => reject(err));
        }),

    confirmBillingSetup: (variables) =>
        new Promise((resolve, reject) => {
            if (variables.cmsId) variables.cmsId = parseInt(variables.cmsId);

            apolloClient
                .mutate({
                    mutation: ConfirmBillingSetupGql,
                    variables,
                })
                .then(({ data }) => resolve(data.ConfirmBillingSetup))
                .catch((err) => reject(err));
        }),

    enableAutoBilling: (cmsId) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: EnableAutoBillingGql,
                    variables: { cmsId: parseInt(cmsId) },
                })
                .then(({ data }) => resolve(data.EnableAutoBilling))
                .catch((err) => reject(err));
        }),

    fetchInvoices: (cmsId) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: BillingInvoicesGql,
                    variables: { cmsId: parseInt(cmsId) },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.BillingInvoices))
                .catch((err) => reject(err));
        }),

    // These URLs bypass Apollo, so the caller hands in a token it got from
    // ensureFreshToken() (see helpers); the stored one may have expired.
    billingInvoicePdfUrl(invoiceId, token) {
        return `${PREVIEW_ENDPOINT}billing-invoice-pdf/${invoiceId}?token=${token}`;
    },

    // The one-off invoice as it would print, from positions that are not written yet.
    billingInvoiceDraftUrl(cmsId, positions, token) {
        return `${PREVIEW_ENDPOINT}billing-invoice-draft/${parseInt(cmsId)}?token=${token}&positions=${encodeURIComponent(JSON.stringify(positions))}`;
    },
};
